// EventsSection.tsx — onglet « Événements » du Dashboard (super-admins, 2026-10-19)
//
// Trois vues sur une même période : les événements de tous les créateurs (notes, chaînes
// verrouillées ou éteintes, vidéos désactivées ou rétablies), les alertes système (transcodages
// en échec ou bloqués, contenus introuvables) et les alertes sécurité (échecs d'authentification
// répétés, adresses bannies, comptes verrouillés, signalements à traiter). Les alertes ne sont
// pas stockées : le serveur les calcule à chaque lecture. Données : admin-api DashboardEventsController.

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import styles from './EventsSection.module.scss';
import { Segment, plural, ago } from './dashboardShared';
import { getDashboardEvents, DashboardEvents, DashboardAlert, DashboardCreatorEvent, AlertSeverity } from '../../../backendServices/adminManagment/api_adminEvents';
import { KIND_LABEL } from '../adminManagment/AdminEventsTab';

type View     = 'events' | 'system' | 'security';
type Severity = 'all' | AlertSeverity;

const REFRESH_MS = 60_000;
const PERIODS = [ ['1', '24 h'], ['7', '7 jours'], ['30', '30 jours'], ['90', '90 jours'] ] as const;
const SEVERITY_LABEL: Record<AlertSeverity, string> = { critical: 'CRITIQUE', warning: 'À SURVEILLER', info: 'INFO' };
const RAIL  : Record<AlertSeverity, string> = { critical: styles.sCritical, warning: styles.sWarning, info: styles.sInfo };
const BADGE : Record<AlertSeverity, string> = { critical: styles.bCritical, warning: styles.bWarning, info: styles.bInfo };

/** Couleur d'un type d'événement : en rouge ce qui coupe un contenu, en vert ce qui le rend. */
const eventTone = (kind: string) =>
  ['channel_lock', 'channel_stop', 'video_disable'].includes(kind) ? 'bCritical' : ['channel_unlock', 'video_enable'].includes(kind) ? 'bGreen' : 'bNeutral';
const eventRail = (kind: string) =>
  ['channel_lock', 'channel_stop', 'video_disable'].includes(kind) ? styles.sCritical : ['channel_unlock', 'video_enable'].includes(kind) ? styles.sGreen : '';

const dateTime = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const Detail: React.FC<{ title: string; rows: [string, React.ReactNode][]; text?: string; onClose: () => void }> = ({ title, rows, text, onClose }) => {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);
  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label={title} onMouseDown={e => e.stopPropagation()}>
        <h3>{title}</h3>
        <dl className={styles.dl}>{rows.filter(([, v]) => v).map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}</dl>
        {text && <div className={styles.full}>{text}</div>}
        <div className={styles.actions}><button type="button" className={styles.btn} onClick={onClose} autoFocus>Fermer</button></div>
      </div>
    </div>
  );
};

const EventsSection: React.FC<{ adminId: number | undefined }> = ({ adminId }) => {
  const [ view   , setView   ] = useState<View>('events');
  const [ days   , setDays   ] = useState<'1' | '7' | '30' | '90'>('7');
  const [ query  , setQuery  ] = useState('');
  const [ sev    , setSev    ] = useState<Severity>('all');
  const [ kind   , setKind   ] = useState<string>('all');
  const [ data   , setData   ] = useState<DashboardEvents | null>(null);
  const [ error  , setError  ] = useState('');
  const [ loading, setLoading ] = useState(true);
  const [ detail , setDetail ] = useState<{ title: string; rows: [string, React.ReactNode][]; text?: string } | null>(null);

  const load = useCallback(async () => {
    if (!adminId) { setError('Compte non identifié : reconnectez-vous.'); setLoading(false); return; }
    const res = await getDashboardEvents(adminId, Number(days));
    if (res.ok) { setData(res.data); setError(''); } else setError(res.error);
    setLoading(false);
  }, [adminId, days]);

  useEffect(() => {
    setLoading(true);
    load();
    const timer = window.setInterval(load, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const q = query.trim().toLowerCase();
  const has = (...parts: (string | null | undefined)[]) => !q || parts.join(' ').toLowerCase().includes(q);

  const events = useMemo(() => (data?.events ?? []).filter(e =>
    (kind === 'all' || e.kind === kind) &&
    has(e.creatorName, e.author, e.channelName, e.videoName, e.comment, e.ip, KIND_LABEL[e.kind])), [data, kind, q]);   // eslint-disable-line react-hooks/exhaustive-deps

  const alerts = useMemo(() => ((view === 'system' ? data?.system : data?.security) ?? []).filter(a =>
    (sev === 'all' || a.severity === sev) && has(a.title, a.detail, a.creatorName, a.ip, a.location?.label, a.location?.region, a.location?.isp, a.location?.as, a.client?.device, a.client?.os, a.client?.browser, a.client?.app, a.userAgent, a.subject?.name)), [data, view, sev, q]);   // eslint-disable-line react-hooks/exhaustive-deps

  const critical = (list: DashboardAlert[] | undefined) => (list ?? []).filter(a => a.severity === 'critical').length;
  const kindsPresent = useMemo(() => Array.from(new Set((data?.events ?? []).map(e => e.kind))), [data]);
  const shownCount = view === 'events' ? events.length : alerts.length;

  const openEvent = (e: DashboardCreatorEvent) => setDetail({
    title: KIND_LABEL[e.kind] ?? e.kind,
    rows: [ ['Créateur', e.creatorName || `n° ${e.creatorId}`], ['À l\'origine', e.author], ['Date et heure', dateTime(e.at)], ['Adresse IP', e.ip],
            ['Chaîne', e.channelName], ['Vidéo', e.videoName ? `n° ${e.videoId} — « ${e.videoName} »` : null] ],
    text: e.comment,
  });
  const openAlert = (a: DashboardAlert) => setDetail({
    title: a.title,
    rows: [ ['Niveau', SEVERITY_LABEL[a.severity]], ['Dernier constat', dateTime(a.at)], ['Créateur', a.creatorName], ['Adresse IP', a.ip], ['Lieu', a.location ? [a.location.label, a.location.region].filter(Boolean).join(' — ') : null], ['Fournisseur d\'accès', a.location?.isp], ['Réseau (AS)', a.location?.as],
            ['Appareil', a.client?.device], ['Système', a.client?.os], ['Navigateur', a.client?.browser], ['Application', a.client?.app],
            ['User-Agent', a.userAgent ? <code className={styles.mono} style={{ wordBreak: 'break-all' }}>{a.userAgent}</code> : null],
            ['Appareil vu', a.client ? `${dateTime(a.client.seenAt)} (${a.client.source})` : (a.ip ? 'Aucun : cette adresse n\'a pas été vue sur le portail ni la régie (les lecteurs IPTV n\'envoient pas ces informations).' : null)],
            ['Vidéo', a.subject?.type === 'video' ? `n° ${a.subject.id} — « ${a.subject.name} »` : null] ],
    text: a.detail,
  });

  const tile = (v: View, label: string, total: number | undefined, sub: string, crit?: number) => (
    <button type="button" className={`${styles.kind} ${view === v ? styles.kindOn : ''}`} aria-pressed={view === v} onClick={() => { setView(v); setSev('all'); }}>
      <span className={styles.kindLabel}>{label}</span>
      <span className={styles.kindValue}>{total ?? '–'}{!!crit && <span className={styles.pillRed}>{crit} critique{crit > 1 ? 's' : ''}</span>}</span>
      <span className={styles.kindSub}>{sub}</span>
    </button>
  );

  return (
    <div className={styles.root}>
      <div className={styles.kinds}>
        {tile('events'  , 'Événements des créateurs', data?.events.length, 'chaînes, vidéos, notes')}
        {tile('system'  , 'Alertes système'          , data?.system.length, 'transcodage, contenus introuvables', critical(data?.system))}
        {tile('security', 'Alertes sécurité'         , data?.security.length, 'authentification, IP, comptes, signalements', critical(data?.security))}
      </div>

      <div className={styles.bar}>
        <div className={styles.search}>
          <svg className={styles.searchIcon} width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="7" cy="7" r="5"/><path d="M11 11l4 4"/></svg>
          <input className={styles.input} type="text" value={query} placeholder="Créateur, vidéo, IP, texte…" aria-label="Rechercher" onChange={e => setQuery(e.target.value)} />
          {query && <button type="button" className={styles.clear} onClick={() => setQuery('')} aria-label="Effacer la recherche">✕</button>}
        </div>
        <Segment label="Période" value={days} onChange={setDays} choices={PERIODS} />
        {view === 'events'
          ? <Segment label="Type" value={kind} onChange={setKind}
                     choices={[ ['all', 'Tous'] as const, ...kindsPresent.map(k => [k, KIND_LABEL[k] ?? k] as const) ]} />
          : <Segment label="Niveau" value={sev} onChange={setSev}
                     choices={[ ['all', 'Tous'], ['critical', 'Critiques'], ['warning', 'À surveiller'], ['info', 'Infos'] ] as const} />}
        <span className={styles.spacer} />
        <span className={styles.count} aria-live="polite"><strong>{plural(shownCount, view === 'events' ? 'événement' : 'alerte')}</strong></span>
        <button type="button" className={styles.refresh} onClick={() => { setLoading(true); load(); }} title="Actualiser">↻</button>
      </div>

      {error && <div className={styles.error} role="alert">{error}</div>}
      {data && Object.keys(data.errors).length > 0 &&
        <div className={styles.warn}>Certaines sources n'ont pas pu être lues : {Object.keys(data.errors).join(', ')}.</div>}

      {loading && !data && [0, 1, 2].map(i => <div key={i} className={styles.skeleton} />)}

      {!loading && !error && shownCount === 0 && (
        <div className={styles.empty}>
          <strong>{view === 'events' ? 'Aucun événement' : view === 'system' ? 'Aucune alerte système' : 'Aucune alerte sécurité'}</strong>
          <span>{q || sev !== 'all' || kind !== 'all' ? 'Aucun résultat avec ces filtres.' : 'Rien à signaler sur cette période.'}</span>
          {(q || sev !== 'all' || kind !== 'all') && <button type="button" className={styles.btn} onClick={() => { setQuery(''); setSev('all'); setKind('all'); }}>Réinitialiser les filtres</button>}
        </div>
      )}

      <div className={styles.list}>
        {view === 'events' && events.map(e => (
          <article key={e.id} className={styles.item}>
            <span className={`${styles.rail} ${eventRail(e.kind)}`} />
            <div className={styles.body}>
              <div className={styles.head}>
                <span className={`${styles.badge} ${styles[eventTone(e.kind)]}`}>{(KIND_LABEL[e.kind] ?? e.kind).toUpperCase()}</span>
                <span className={styles.title}>{e.creatorName || `Créateur n° ${e.creatorId}`}</span>
                <span className={styles.when} title={dateTime(e.at)}>{ago(e.at)}</span>
              </div>
              <p className={`${styles.detail} ${styles.clamp}`}>{e.comment}</p>
              <div className={styles.facts}>
                <span className={styles.chip}><em>Par</em><b>{e.author}</b></span>
                {e.channelName && <span className={styles.chip}><em>Chaîne</em><b>{e.channelName}</b></span>}
                {e.videoName && <span className={styles.chip}><em>Vidéo n° {e.videoId}</em><b>{e.videoName}</b></span>}
                {e.ip && <span className={`${styles.chip} ${styles.mono}`}><em>IP</em><b>{e.ip}</b></span>}
              </div>
            </div>
            <div className={styles.side}><button type="button" className={styles.btn} onClick={() => openEvent(e)}>Détail</button></div>
          </article>
        ))}

        {view !== 'events' && alerts.map(a => (
          <article key={a.id} className={styles.item}>
            <span className={`${styles.rail} ${RAIL[a.severity]}`} />
            <div className={styles.body}>
              <div className={styles.head}>
                <span className={`${styles.badge} ${BADGE[a.severity]}`}>{SEVERITY_LABEL[a.severity]}</span>
                <span className={styles.title}>{a.title}</span>
                <span className={styles.when} title={dateTime(a.at)}>{ago(a.at)}</span>
              </div>
              <p className={`${styles.detail} ${styles.clamp}`}>{a.detail}</p>
              <div className={styles.facts}>
                {a.creatorName && <span className={styles.chip}><em>Créateur</em><b>{a.creatorName}</b></span>}
                {a.subject && <span className={styles.chip}><em>Vidéo n° {a.subject.id}</em><b>{a.subject.name}</b></span>}
                {a.ip && <span className={`${styles.chip} ${styles.mono}`}><em>IP</em><b>{a.ip}</b></span>}
                {a.ip && <span className={styles.chip}><em>Lieu</em><b>{a.location?.label ?? 'inconnu'}</b></span>}
                {a.ip && <span className={styles.chip} title={a.location?.as ?? undefined}><em>Fournisseur</em><b>{a.location?.isp ?? 'inconnu'}</b></span>}
                {a.ip && (() => {
                  const c = a.client;
                  const tip = c ? (c.source === 'lecteur Xtream' ? `Déduit du User-Agent du lecteur Xtream, vu le ${dateTime(c.seenAt)}.` : `Vu le ${dateTime(c.seenAt)} (${c.source}). Peut différer du lecteur qui a provoqué l'alerte.`) : "Aucun appareil connu : les lecteurs IPTV (box, VLC…) n'envoient pas ces informations.";
                  return <>
                    <span className={styles.chip} title={tip}><em>Appareil</em><b>{c?.device ?? 'inconnu'}</b></span>
                    <span className={styles.chip} title={tip}><em>Système</em><b>{c?.os ?? 'inconnu'}</b></span>
                    <span className={styles.chip} title={tip}><em>Navigateur</em><b>{c?.browser ?? (c?.app ? c.app : 'inconnu')}</b></span>
                    {a.userAgent && <span className={`${styles.chip} ${styles.mono}`} title={a.userAgent}><em>User-Agent</em><b>{a.userAgent}</b></span>}
                  </>;
                })()}
              </div>
            </div>
            <div className={styles.side}><button type="button" className={styles.btn} onClick={() => openAlert(a)}>Détail</button></div>
          </article>
        ))}
      </div>

      {detail && <Detail {...detail} onClose={() => setDetail(null)} />}
    </div>
  );
};

export default EventsSection;
